import React from "react";
import {CheckCircleIcon, DocumentTextIcon, ChevronDownIcon, XMarkIcon} from "@heroicons/react/24/outline";
import useTranslate from "../../../core/languages/hooks/useTranslate";
import ButtonPrimary from "../../../ui/shared/Button/ButtonPrimary";
import useEscapeKey from "../../hooks/useEscapeKey";
import useScrollProgress from "../../hooks/useScrollProgress";

interface PolicyModalProps {
    isOpen: boolean;
    onAccept: () => void;
    onClose?: () => void;
    titleKey: string;
    content: string;
}

// ─── Sub-components ────────────────────────────────────────────────────────────

const ProgressBar: React.FC<{ progress: number }> = ({progress}) => (
    <div className="h-1 w-full bg-neutral-200 dark:bg-neutral-800 overflow-hidden">
        <div
            className="h-full bg-red-600"
            style={{
                transform: `scaleX(${progress / 100})`,
                transformOrigin: "0 0",
                willChange: "transform",
                transition: "transform 150ms ease-out",
            }}
        />
    </div>
);

const ModalHeader: React.FC<{ titleKey: string; onClose?: () => void }> = ({titleKey, onClose}) => {
    const t = useTranslate();
    return (
        <div className="flex items-start gap-4 border-b border-neutral-200 px-6 py-5 dark:border-neutral-800">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 dark:bg-red-500/10">
                <DocumentTextIcon className="h-6 w-6 text-red-600"/>
            </div>
            <div className="flex-1">
                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
                    {t(titleKey)}
                </h2>
                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    {t("terms.labels.mustReadAllDocument")}
                </p>
            </div>
            {onClose && (
                <button
                    type="button"
                    onClick={onClose}
                    className="flex-shrink-0 rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 dark:hover:bg-neutral-800 dark:hover:text-neutral-300"
                    aria-label={t("common.actions.close")}
                >
                    <XMarkIcon className="h-5 w-5"/>
                </button>
            )}
        </div>
    );
};

const ScrollBottomBanner: React.FC = () => {
    const t = useTranslate();
    return (
        <div className="mt-8 flex items-center gap-3 rounded-2xl border border-green-200 bg-green-50 px-4 py-4 text-green-700 dark:border-green-500/20 dark:bg-green-500/10 dark:text-green-400">
            <CheckCircleIcon className="h-6 w-6 flex-shrink-0"/>
            <p className="font-medium">{t("terms.labels.documentRevised")}</p>
        </div>
    );
};

const ScrollHelper: React.FC<{ onScrollToEnd: () => void }> = ({onScrollToEnd}) => {
    const t = useTranslate();
    return (
        <button
            type="button"
            onClick={onScrollToEnd}
            className="absolute bottom-28 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-medium text-neutral-700 shadow-lg ring-1 ring-black/5 transition-colors hover:bg-white dark:bg-neutral-800/95 dark:text-neutral-200 dark:hover:bg-neutral-800"
        >
            <ChevronDownIcon className="h-4 w-4 animate-bounce"/>
            {t("terms.labels.scrollToTheEnd")}
        </button>
    );
};

const ModalFooter: React.FC<{ progress: number; canAccept: boolean; onAccept: () => void }> = ({
    progress,
    canAccept,
    onAccept,
}) => {
    const t = useTranslate();
    return (
        <div className="flex items-center justify-between border-t border-neutral-200 bg-white px-6 py-5 dark:border-neutral-800 dark:bg-neutral-900">
            <div className="text-sm text-neutral-500 dark:text-neutral-400">
                {Math.round(progress)}% {t("terms.labels.revised")}
            </div>
            {canAccept ? (
                <ButtonPrimary onClick={onAccept}>
                    {t("common.actions.accept")}
                </ButtonPrimary>
            ) : (
                <ButtonPrimary
                    disabled
                    className="bg-neutral-400 dark:bg-neutral-600 hover:bg-neutral-400 dark:hover:bg-neutral-600 !text-neutral-600 dark:!text-neutral-300 cursor-not-allowed opacity-60"
                >
                    {t("terms.labels.readCompleteDocument")}
                </ButtonPrimary>
            )}
        </div>
    );
};

// ─── Main component ─────────────────────────────────────────────────────────────

const LegalModal: React.FC<PolicyModalProps> = ({isOpen, onAccept, onClose, titleKey, content}) => {
    const {contentRef, handleScroll, scrollToBottom, hasScrolledToBottom, progress} = useScrollProgress(isOpen);
    useEscapeKey(onClose, isOpen);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-neutral-900"
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
            >
                <ProgressBar progress={progress}/>

                <ModalHeader titleKey={titleKey} onClose={onClose}/>

                <div
                    ref={contentRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto px-6 py-6"
                >
                    <div
                        className="prose dark:prose-invert max-w-none text-sm text-neutral-700 dark:text-neutral-300 space-y-4"
                        dangerouslySetInnerHTML={{__html: content}}
                    />
                    {hasScrolledToBottom && <ScrollBottomBanner/>}
                </div>

                {!hasScrolledToBottom && <ScrollHelper onScrollToEnd={scrollToBottom}/>}

                <ModalFooter progress={progress} canAccept={hasScrolledToBottom} onAccept={onAccept}/>
            </div>
        </div>
    );
};

export default LegalModal;
